// THROWAWAY — bug reporter widget, remove before go-live (P12)
//
// A small, near-invisible floating corner button that expands into a panel for
// filing a quick bug/issue (free text + optional screenshot). On submit it
// POSTs to the diagnostic BFF route `POST /api/_diag/bug-report`, which creates
// a GitLab issue SYNCHRONOUSLY in the request path and returns the issue URL —
// the widget then shows a direct link to the filed issue.
//
// Visibility is RUNTIME-ONLY, driven purely by the server's tester ALLOW-LIST:
// the widget queries `/api/_diag/bug-report/config` and renders only if that
// returns `{ enabled: true }` for the current authenticated principal. There is
// NO build-time flag — dev and staging share ONE built image, so visibility
// MUST be runtime (the server's `bugReporterEnabled` flag + the allow-list,
// staging-only), never baked into the bundle. The list is enforced/owned
// server-side; the SPA never sees it. Any failure ⇒ the widget stays hidden.
//
// Self-contained: this whole feature is THIS file + `server/src/dev/bug-report.ts`
// + `bug-allowlist.ts` + the single `<BugReporter />` mount in
// `components/AppLayout.tsx` (plus the staging env/secret wiring + the GitLab
// token & its Secrets Manager secret). Delete those to fully remove it. NOT a
// screen — an overlay; it is NOT in the screen registry / visual-tour manifest.
//
// Designed to be invisible to the Playwright suites: a tiny, low-opacity button
// pinned bottom-right with a stable `data-testid="bug-reporter"`, a high z-index
// so it never sits under content, and `pointer-events` confined to itself so it
// cannot intercept clicks elsewhere. The panel only renders when opened. When
// the server reports it disabled (the CI/e2e default — BUG_REPORTER_ENABLED
// unset) it renders nothing at all.

import React, { useEffect, useState } from 'react';

const ENDPOINT = '/api/_diag/bug-report';
const CONFIG_ENDPOINT = '/api/_diag/bug-report/config';
const MAX_SCREENSHOT_PX = 1280; // downscale long edge to keep the payload small

/** Reads a File into a downscaled JPEG data URL (keeps the JSON body small). */
async function fileToDownscaledDataUrl(file: File): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });
  // Best-effort downscale via canvas; fall back to the raw data URL if the
  // browser/test environment lacks image decoding.
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('decode failed'));
      i.src = dataUrl;
    });
    const scale = Math.min(
      1,
      MAX_SCREENSHOT_PX / Math.max(img.width, img.height),
    );
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUrl;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.7);
  } catch {
    return dataUrl;
  }
}

type Status =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'ok'; issueUrl?: string }
  | { kind: 'not-configured' }
  | { kind: 'error'; message: string };

export function BugReporter(): React.JSX.Element | null {
  // Visibility is RUNTIME-ONLY (no build flag): render the widget only when the
  // server reports the bug reporter enabled for this principal. That single
  // `/config` probe folds in BOTH the server's bugReporterEnabled flag (the
  // routes are only mounted on staging) and the tester allow-list. Any failure
  // ⇒ stay hidden (fail-safe), so dev/CI/prod render nothing.
  const [allowed, setAllowed] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    void fetch(CONFIG_ENDPOINT, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) return false;
        const data = (await res.json().catch(() => ({}))) as {
          enabled?: boolean;
        };
        return data.enabled === true;
      })
      .catch(() => false)
      .then((ok) => {
        if (!cancelled) setAllowed(ok);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (allowed !== true) return null;
  return <BugReporterPanel />;
}

function BugReporterPanel(): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [jira, setJira] = useState('');
  const [file, setFile] = useState<File | undefined>();
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!text.trim() || status.kind === 'sending') return;
    setStatus({ kind: 'sending' });

    let screenshotDataUrl: string | undefined;
    if (file) {
      try {
        screenshotDataUrl = await fileToDownscaledDataUrl(file);
      } catch {
        screenshotDataUrl = undefined; // best-effort; report still sent
      }
    }

    // Auto-captured client context — current route + a small environment blob.
    const context = {
      screenId: window.location.pathname,
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      capturedAt: new Date().toISOString(),
    };

    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          text: text.trim(),
          jira: jira.trim() || undefined,
          url: window.location.href,
          context,
          screenshotDataUrl,
        }),
      });
      if (res.status === 503) {
        setStatus({ kind: 'not-configured' });
        return;
      }
      if (!res.ok) {
        setStatus({ kind: 'error', message: `Failed (HTTP ${res.status}).` });
        return;
      }
      const data = (await res.json().catch(() => ({}))) as {
        issueUrl?: string;
      };
      setStatus({ kind: 'ok', issueUrl: data.issueUrl });
      setText('');
      setFile(undefined);
    } catch {
      setStatus({ kind: 'error', message: 'Network error.' });
    }
  }

  return (
    <div
      data-testid="bug-reporter"
      style={{
        position: 'fixed',
        right: 8,
        bottom: 8,
        zIndex: 2147483000,
        // Confine pointer events to the actual controls so the wrapper never
        // intercepts clicks meant for the page behind it.
        pointerEvents: 'none',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {open && (
        <form
          data-testid="bug-reporter-panel"
          onSubmit={submit}
          style={{
            pointerEvents: 'auto',
            width: 280,
            marginBottom: 8,
            padding: 12,
            borderRadius: 8,
            background: '#ffffff',
            border: '1px solid #d1d5db',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            fontSize: 13,
            color: '#111827',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            Report an issue
          </div>
          <div
            data-testid="bug-reporter-pii-warning"
            role="note"
            style={{
              marginBottom: 8,
              padding: '6px 8px',
              borderRadius: 4,
              background: '#fef3c7',
              border: '1px solid #f59e0b',
              color: '#92400e',
              fontSize: 11,
              lineHeight: 1.35,
            }}
          >
            Internal diagnostics only — do NOT enter or capture customer PII,
            PANs, or account details.
          </div>
          <textarea
            data-testid="bug-reporter-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="What happened / what did you expect?"
            rows={4}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              resize: 'vertical',
              padding: 6,
              border: '1px solid #d1d5db',
              borderRadius: 4,
              fontSize: 13,
            }}
          />
          <input
            data-testid="bug-reporter-jira"
            value={jira}
            onChange={(e) => setJira(e.target.value)}
            placeholder="JIRA ticket (optional, e.g. CCT-1234)"
            aria-label="JIRA ticket (optional)"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              marginTop: 8,
              padding: 6,
              border: '1px solid #d1d5db',
              borderRadius: 4,
              fontSize: 13,
            }}
          />
          <label
            style={{
              display: 'block',
              marginTop: 8,
              fontSize: 12,
              color: '#374151',
            }}
          >
            Screenshot (optional)
            <input
              data-testid="bug-reporter-file"
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? undefined)}
              style={{ display: 'block', marginTop: 4, fontSize: 12 }}
            />
          </label>
          <div
            style={{
              marginTop: 10,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <button
              data-testid="bug-reporter-submit"
              type="submit"
              disabled={!text.trim() || status.kind === 'sending'}
              style={{
                padding: '5px 10px',
                borderRadius: 4,
                border: 'none',
                background: '#1f2937',
                color: '#fff',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {status.kind === 'sending' ? 'Sending…' : 'Send'}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                padding: '5px 10px',
                borderRadius: 4,
                border: '1px solid #d1d5db',
                background: '#fff',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
          {status.kind === 'ok' && (
            <div
              data-testid="bug-reporter-result"
              style={{ marginTop: 8, color: '#047857', fontSize: 12 }}
            >
              {status.issueUrl ? (
                <>
                  Thanks — filed as{' '}
                  <a
                    data-testid="bug-reporter-issue-link"
                    href={status.issueUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#047857', textDecoration: 'underline' }}
                  >
                    a GitLab issue
                  </a>
                  .
                </>
              ) : (
                'Thanks — reported.'
              )}
            </div>
          )}
          {status.kind === 'not-configured' && (
            <div
              data-testid="bug-reporter-result"
              style={{ marginTop: 8, color: '#92400e', fontSize: 12 }}
            >
              Bug reporter is not configured in this environment.
            </div>
          )}
          {status.kind === 'error' && (
            <div
              data-testid="bug-reporter-result"
              style={{ marginTop: 8, color: '#b91c1c', fontSize: 12 }}
            >
              {status.message}
            </div>
          )}
        </form>
      )}

      <button
        data-testid="bug-reporter-toggle"
        type="button"
        aria-label="Report a bug or issue"
        title="Report a bug or issue"
        onClick={() => {
          setStatus({ kind: 'idle' });
          setOpen((o) => !o);
        }}
        style={{
          pointerEvents: 'auto',
          float: 'right',
          width: 26,
          height: 26,
          borderRadius: '50%',
          border: '1px solid rgba(0,0,0,0.15)',
          background: 'rgba(31,41,55,0.18)',
          color: 'rgba(17,24,39,0.65)',
          fontSize: 13,
          lineHeight: '24px',
          textAlign: 'center',
          cursor: 'pointer',
          opacity: open ? 0.9 : 0.35,
          padding: 0,
        }}
      >
        🐞
      </button>
    </div>
  );
}
